/** A run of admin-authored copy; `href` is present only on a link run. */
export interface InlineLinkSegment {
  text: string;
  href?: string;
}

const MARKDOWN_LINK = /\[([^\]\n]+)\]\(([^()\s]+)\)/g;
const LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function safeHref(raw: string) {
  try {
    const url = new URL(raw);
    return LINK_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * Splits admin-authored copy into text and link runs so it can render without HTML.
 * `[label](url)` becomes a link only for an absolute `http`, `https` or `mailto` URL;
 * any other target (relative, `javascript:`, malformed) degrades to its label as plain
 * text. Adjacent text runs are merged, and an empty string yields no runs.
 */
export function parseInlineLinks(source: string): InlineLinkSegment[] {
  const segments: InlineLinkSegment[] = [];
  const pushText = (text: string) => {
    if (!text) return;
    const last = segments.at(-1);
    if (last && last.href === undefined) {
      last.text += text;
    } else {
      segments.push({ text });
    }
  };

  let cursor = 0;
  for (const match of source.matchAll(MARKDOWN_LINK)) {
    const [whole, label = '', rawHref = ''] = match;
    pushText(source.slice(cursor, match.index));
    const href = safeHref(rawHref);
    if (href) {
      segments.push({ text: label, href });
    } else {
      pushText(label);
    }
    cursor = match.index + whole.length;
  }
  pushText(source.slice(cursor));

  return segments;
}
